// ===================================================================
// KAIXO - CRMActivitySidebar (alias: sidebar de Notas en kaixoweb)
// ===================================================================
// Reemplaza la columna 1 (lista de chats) cuando el panel activo es
// Impulsa en kaixo web. Muestra el histórico cronológico de NOTAS que
// los agentes han escrito sobre los leads — el "diario" del equipo.
//
// Filtros: agente, fecha (desde / hasta), anuncio (canal origen), lead.
// Click en una nota → window.dispatchEvent('crm:open_lead', {leadId})
// que el CRMModal de la columna 2 escucha para abrir el lead.
//
// Realtime: escucha sockets crm:note_created/deleted y refresca solo.
// ===================================================================

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { crmGetNotes, crmGetAgents, crmGetLeads } from '../../services/crmApi';
import { Icons } from '../Icons/Icons';
import { getSocket } from '../../services/socket';
import api from '../../services/api';
import './CRMActivitySidebar.css';

const LABELS = {
    español: {
        title: 'Historial de notas', search: 'Buscar texto...', allAgents: 'Todos los agentes',
        allLeads: 'Todos los contactos', allAds: 'Todos los anuncios', empty: 'Sin notas todavía',
        loading: 'Cargando...', refresh: 'Actualizar', filters: 'Filtros',
        today: 'Hoy', yesterday: 'Ayer', byShort: 'por',
        agent: 'Agente', lead: 'Contacto', ad: 'Anuncio', dateFrom: 'Desde', dateTo: 'Hasta',
        clearFilters: 'Limpiar'
    },
    english: {
        title: 'Notes history', search: 'Search text...', allAgents: 'All agents',
        allLeads: 'All contacts', allAds: 'All ads', empty: 'No notes yet',
        loading: 'Loading...', refresh: 'Refresh', filters: 'Filters',
        today: 'Today', yesterday: 'Yesterday', byShort: 'by',
        agent: 'Agent', lead: 'Contact', ad: 'Ad', dateFrom: 'From', dateTo: 'To',
        clearFilters: 'Clear'
    }
};
function getLang() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return u.language || 'español';
    } catch { return 'español'; }
}
function lx() { return LABELS[getLang()] || LABELS.español; }

const TYPE_ICONS = { call:'📞', message:'💬', visit:'📍', email:'✉️', meeting:'🤝', other:'⭐' };

function fmtTime(iso, lang) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString(lang === 'english' ? undefined : 'es-ES', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
}
function dayKey(iso) {
    try {
        const d = new Date(iso);
        d.setHours(0,0,0,0);
        return d.toISOString().slice(0, 10);
    } catch { return 'unknown'; }
}
function dayLabel(iso, labels, lang) {
    try {
        const today = new Date(); today.setHours(0,0,0,0);
        const yesterday = new Date(today.getTime() - 86400000);
        const d = new Date(iso); d.setHours(0,0,0,0);
        if (d.getTime() === today.getTime()) return labels.today;
        if (d.getTime() === yesterday.getTime()) return labels.yesterday;
        return d.toLocaleDateString(lang === 'english' ? undefined : 'es-ES', {
            day: 'numeric', month: 'short',
            year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
        });
    } catch { return ''; }
}

export default function CRMActivitySidebar() {
    const [notes, setNotes] = useState([]);
    const [agents, setAgents] = useState([]);
    const [leads, setLeads] = useState([]);
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState('');
    const [agentFilter, setAgentFilter] = useState('');
    const [leadFilter, setLeadFilter] = useState('');
    const [adFilter, setAdFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const labels = lx();
    const lang = getLang();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (agentFilter) params.agentId = agentFilter;
            if (leadFilter) params.leadId = leadFilter;
            if (adFilter) params.channelId = adFilter;
            if (dateFrom) params.since = new Date(dateFrom + 'T00:00:00').toISOString();
            if (dateTo) params.until = new Date(dateTo + 'T23:59:59').toISOString();
            if (search.trim()) params.q = search.trim();
            const res = await crmGetNotes(params);
            setNotes(res?.data?.notes || []);
        } catch (e) { console.warn('crmGetNotes:', e?.message); }
        setLoading(false);
    }, [agentFilter, leadFilter, adFilter, dateFrom, dateTo, search]);

    // Cargar agentes + leads + canales (para los selects de filtro)
    useEffect(() => {
        crmGetAgents().then(r => setAgents(r?.data?.agents || [])).catch(() => {});
        crmGetLeads({ limit: 200 }).then(r => setLeads(r?.data?.leads || [])).catch(() => {});
        api.get('/v2/channels/mine').then(r => {
            const list = r?.data?.channels || [];
            setAds(list.filter(c => c.type === 'business'));
        }).catch(() => {});
    }, []);

    // Recarga al cambiar filtros — debounce ligero por search
    useEffect(() => {
        const t = setTimeout(() => load(), search ? 300 : 0);
        return () => clearTimeout(t);
    }, [load, search]); // eslint-disable-line

    // Realtime — registramos el listener UNA VEZ y usamos un ref a la
    // función load actual. Antes se re-registraba con cada keystroke del
    // search porque load era dependencia y cambiaba con search/filters.
    const loadRef = useRef(load);
    useEffect(() => { loadRef.current = load; }, [load]);
    useEffect(() => {
        const s = typeof getSocket === 'function' ? getSocket() : null;
        if (!s) return;
        const handler = () => { try { loadRef.current?.(); } catch {} };
        s.on('crm:note_created', handler);
        s.on('crm:note_deleted', handler);
        return () => {
            try {
                s.off('crm:note_created', handler);
                s.off('crm:note_deleted', handler);
            } catch {}
        };
    }, []);

    // Agrupar por día
    const grouped = useMemo(() => {
        const map = new Map();
        for (const n of notes) {
            const key = dayKey(n.createdAt);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(n);
        }
        return Array.from(map.entries());
    }, [notes]);

    const handleClick = (note) => {
        const leadId = note.lead?._id || note.lead;
        if (!leadId) return;
        try {
            window.dispatchEvent(new CustomEvent('crm:open_lead', { detail: { leadId } }));
        } catch {}
    };

    const clearFilters = () => {
        setAgentFilter(''); setLeadFilter(''); setAdFilter('');
        setDateFrom(''); setDateTo(''); setSearch('');
    };
    const hasActiveFilters = agentFilter || leadFilter || adFilter || dateFrom || dateTo || search;

    return (
        <div className="crm-act-sidebar">
            <div className="crm-act-head">
                <h3>{labels.title}</h3>
                <button className={`crm-act-btn ${hasActiveFilters ? 'has-active' : ''}`} onClick={() => setShowFilters(s => !s)} title={labels.filters}>
                    <Icons.Search size={14} />
                </button>
                <button className="crm-act-btn" onClick={load} disabled={loading} title={labels.refresh}>
                    {loading ? '…' : '↻'}
                </button>
            </div>

            <div className="crm-act-search">
                <input
                    type="text"
                    placeholder={labels.search}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            {showFilters && (
                <div className="crm-act-filters">
                    <div className="crm-act-filter-row">
                        <label>{labels.agent}</label>
                        <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)}>
                            <option value="">{labels.allAgents}</option>
                            {agents.map(a => {
                                const id = a.userId?._id || a.userId || a._id;
                                const name = a.userId?.name || a.name || '—';
                                return <option key={id} value={id}>{name}</option>;
                            })}
                        </select>
                    </div>
                    <div className="crm-act-filter-row">
                        <label>{labels.lead}</label>
                        <select value={leadFilter} onChange={e => setLeadFilter(e.target.value)}>
                            <option value="">{labels.allLeads}</option>
                            {leads.map(l => (
                                <option key={l._id} value={l._id}>
                                    {`${l.firstName || ''} ${l.lastName || ''}`.trim() || '—'}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="crm-act-filter-row">
                        <label>{labels.ad}</label>
                        <select value={adFilter} onChange={e => setAdFilter(e.target.value)}>
                            <option value="">{labels.allAds}</option>
                            {ads.map(c => (
                                <option key={c._id} value={c._id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="crm-act-filter-row">
                        <label>{labels.dateFrom}</label>
                        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                    </div>
                    <div className="crm-act-filter-row">
                        <label>{labels.dateTo}</label>
                        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                    </div>
                    {hasActiveFilters && (
                        <button type="button" className="crm-act-clear" onClick={clearFilters}>
                            {labels.clearFilters} ({notes.length})
                        </button>
                    )}
                </div>
            )}

            <div className="crm-act-body">
                {loading && notes.length === 0 && (
                    <div className="crm-act-empty">{labels.loading}</div>
                )}
                {!loading && grouped.length === 0 && (
                    <div className="crm-act-empty">{labels.empty}</div>
                )}
                {grouped.map(([dKey, items]) => (
                    <div key={dKey} className="crm-act-day">
                        <div className="crm-act-day-head">{dayLabel(items[0].createdAt, labels, lang)}</div>
                        {items.map(n => {
                            const leadName = n.lead
                                ? `${n.lead.firstName || ''} ${n.lead.lastName || ''}`.trim() || '—'
                                : '—';
                            const agentName = n.agent?.name || '—';
                            const icon = TYPE_ICONS[n.interactionType] || TYPE_ICONS.other;
                            return (
                                <div key={n._id} className="crm-note-item" onClick={() => handleClick(n)}>
                                    <div className="crm-note-head-row">
                                        <span className="crm-note-time">{fmtTime(n.createdAt, lang)}</span>
                                        <span className="crm-note-type">{icon}</span>
                                        <span className="crm-note-lead">{leadName}</span>
                                    </div>
                                    <div className="crm-note-text">{n.text}</div>
                                    <div className="crm-note-agent">{labels.byShort} {agentName}</div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}
